import React, { useState, useEffect, ReactNode, PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import ZooHeader from 'components/header/zoo';
import ExpHeader from 'components/header/exp';
import NewExpHeader from 'components/header/exp-v2';
import CommercialHeader from 'components/header/commercial';
import AgntIncHeader from 'agnt/header';
import Footer from 'components/footer/footer';
import AgntIncFooter from 'agnt/footer';
import { FOOTER_CONTENT } from 'agnt/footer/content';
import { AnalyticsController } from 'analytics/analytics-controller';
import { buildClassName } from 'utils/build-class-name';
import LoadingSpinner from 'components/loading-spinner';
import { useFeaturesContext } from 'contexts/features';
import MobileBottomNav from 'components/header/zoo/mobile-bottom-nav';
import { AREA_LISTINGS_ROUTE, RouteName, NEWHOMES_ROUTE, Route } from 'components/dynamic-page/route-matchers';
import { useThemeContext, useUser } from 'contexts';
import styles from './style.module.scss';
import { expInServiceTenant } from 'tenants/definitions/exp/in-service/exp-in-service.tenant';
import { TenantId } from '@tenants/types';
import { isTenantAgntInc, isTenantExpCA, isTenantExpCommercial, isTenantExpCommercialCa, isTenantExpInService, isTenantExpUS, isZoocasaTenant } from '@tenants/utils/which-tenant';
import { zoocasaNavigationConfig } from 'tenants/zoocasa/config/navigation';
import { zoocasaLightNavigationConfig } from '@tenants/definitions/zoocasa-light/navigation-config';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { useIsIpadMini, useIsMobile } from 'hooks/use-size-class';
import { shouldShowAccessibilityWidget } from 'utils/accessibility';
import storage from 'utils/storage';
import { WebVitals } from 'components/web-vitals';
import { useRouterLoading } from 'hooks/use-router-loading';
import { useIsomorphicLayoutEffect } from 'types/react';
import { getTenantById } from '@tenants/manager';
import SearchLayout from 'ui/search-layout';

type LayoutProps = PropsWithChildren<{
  className?: string;
  renderHeader?: () => ReactNode;
  showMobileBottomNav?: boolean;
  routeName?: RouteName;
  isShowing404Page?: boolean;
  showFooter?: boolean;
  noLoadingOnSamePath?: boolean;
  /** Changing value (e.g. hero image URL) forces Layout to re-render so children receive updates */
  contentKey?: string;
  joinExpFabDestination?: string;
}>;

const mobileNavBlackList = [
  '/home-appraisal/property-details/results', '/search', '/explore-exp', '/referrals', '/my-link-my-lead',
  '/sell', '/buy', '/agents-search', '/exp-specialized-divisions', '/experts', '/income', '/relocation', '/reo',
  '/join-exp', '/join-exp-ca', '/join-exp-us',
];

const DefaultRenderHeader = ({
  tenantId,
  routeName,
  isShowing404Page,
  joinExpFabDestination,
}: {
  tenantId: TenantId;
  routeName?: RouteName;
  isShowing404Page?: boolean;
  joinExpFabDestination?: string;
}) => {
  const { features } = useFeaturesContext();
  const useZooHeader = isZoocasaTenant(tenantId);
  const tenant = getTenantById(tenantId);
  const useZoocasaLightHomepage = (tenant?.features?.UseZoocasaLightHomepage ?? false) || (features?.['useZoocasaLightHomepage'] === true);

  // TODO: zoocasa-light may be merged into zoocasa eventually, removing the need for separate nav configs
  const zooNavConfig = useZoocasaLightHomepage ? zoocasaLightNavigationConfig : zoocasaNavigationConfig;
  const useCommercialHeader = isTenantExpCommercial(tenantId) || isTenantExpCommercialCa(tenantId);
  const useExpHeader = isTenantExpCA(tenantId) || isTenantExpUS(tenantId);
  const useExpInServiceHeader = isTenantExpInService(tenantId);
  const useAgntIncHeader = isTenantAgntInc(tenantId);
  const isMobile = useIsMobile();
  const isIpadMini = useIsIpadMini();
  const shouldShowMobileView = isMobile && !isIpadMini;
  const isSearchOrAreaPage = routeName === SEARCH_PAGE_ROUTE_NAME || routeName === AREA_LISTINGS_ROUTE;
  const showAdvancedSearch = shouldShowMobileView && !isSearchOrAreaPage;

  return (
    <>
      {useExpInServiceHeader && <NewExpHeader config={expInServiceTenant.config.header} />}
      {useZooHeader && <ZooHeader routeName={routeName} navConfig={zooNavConfig} useLightTheme={useZoocasaLightHomepage} />}
      {useExpHeader && (
        <ExpHeader
          tenantId={tenantId}
          showAdvancedSearch={showAdvancedSearch}
          isSearchOrAreaPage={isSearchOrAreaPage}
          isShowing404Page={isShowing404Page}
          joinExpFabDestination={joinExpFabDestination}
        />
      )}
      {useCommercialHeader && <CommercialHeader />}
      {useAgntIncHeader && <AgntIncHeader />}
    </>
  );
};

export default function Layout({
  children,
  className = '',
  showMobileBottomNav,
  showFooter = true,
  routeName,
  renderHeader,
  isShowing404Page,
  noLoadingOnSamePath = false,
  contentKey: _contentKey,
  joinExpFabDestination,
}: LayoutProps) {
  const { isLoading } = useRouterLoading({ noLoadingOnSamePath });
  const router = useRouter();
  const { tenantId } = useThemeContext(); // FIXME: Hack to get the tenant id :_(
  const { areDelayedScriptsLoaded, features } = useFeaturesContext();
  const { siteLocation } = useUser();
  const tenant = getTenantById(tenantId);
  const useNewAreaSearchDesign = tenant?.features?.UseNewAreaSearchDesign ?? false;
  const useNewHybridPageDesign = tenant?.features?.UseNewHybridPageDesign ?? false;
  const isSearchOrAreaPage = routeName === SEARCH_PAGE_ROUTE_NAME || routeName === AREA_LISTINGS_ROUTE;

  // Accessibility widget state management (moved from app level)
  const [showAccessibilityWidget, setShowAccessibilityWidget] = useState(shouldShowAccessibilityWidget(tenantId));

  useIsomorphicLayoutEffect(() => {
    const hideAccessibilityWidget = storage.get('accessibilityWidgetClosed');
    // Hide only if value is true
    if (hideAccessibilityWidget) setShowAccessibilityWidget(false);
  }, []);


  let isMobileBottomNavEnabled: boolean;
  if (showMobileBottomNav === undefined) {
    isMobileBottomNavEnabled = !mobileNavBlackList.includes(router.route);
  } else {
    isMobileBottomNavEnabled = showMobileBottomNav;
  }

  useEffect(() => {
    if (areDelayedScriptsLoaded) {
      AnalyticsController?.trackDelayedPageView();
    }

  }, [areDelayedScriptsLoaded]);

  // Footer logic
  const renderFooter = router.route.match(/^((?!\/team\/|\/search).)*$/) && showFooter;
  const footerRoute = router.pathname.includes('/newhomes') ? NEWHOMES_ROUTE : router.route as Route;

  if ((useNewAreaSearchDesign || useNewHybridPageDesign) && isSearchOrAreaPage) {
    return <SearchLayout className={className} showFooter={showFooter} routeName={routeName} renderHeader={renderHeader} isShowing404Page={isShowing404Page} noLoadingOnSamePath={noLoadingOnSamePath} >{children}</SearchLayout>;
  }

  return (
    <>
      <WebVitals />
      {renderHeader ? renderHeader() : (
        <DefaultRenderHeader
          tenantId={tenantId}
          routeName={routeName}
          isShowing404Page={isShowing404Page}
          joinExpFabDestination={joinExpFabDestination}
        />
      )}
      {isLoading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
      {!isLoading && (
        <div className={buildClassName(styles.page, className)}>
          {children}
        </div>
      )}
      {renderFooter && isTenantAgntInc(tenantId) && <AgntIncFooter content={FOOTER_CONTENT} />}
      {renderFooter && !isTenantAgntInc(tenantId) && (
        <Footer
          canShowAccessibilityWidget={showAccessibilityWidget}
          tenantId={tenantId}
          countryCode={siteLocation}
          route={footerRoute}
          features={features}
        />
      )}
      <div className={styles['mobile-nav']}>
        {isMobileBottomNavEnabled && <MobileBottomNav />}
      </div>
    </>
  );
}
